import React, { HTMLInputAutoCompleteAttribute } from "react";
import { FieldValues, Path } from "react-hook-form";

import { Input } from "@/components/Form/Base/input.component";

import { useAutofill } from "../../Utils/useAutofill";
import { BaseField } from "../BaseField/BaseField.component";
import { FormValuesExploded } from "../formField.types";
import { useNumericField } from "./NumericField.hooks";

type NumericFieldProps<T extends FieldValues> = FormValuesExploded<T> & {
    placeholder?: string;
    autoComplete?: HTMLInputAutoCompleteAttribute;
    disabled?: boolean;
    ref?: React.Ref<HTMLInputElement>;
};

const NumericField = <T extends FieldValues>({
    name,
    label,
    microCopy,
    required,
    placeholder,
    autoComplete,
    disabled,
    hideLabel,
    hideMicroCopy,
    ref,
    test
}: NumericFieldProps<T>) => {
    const { handleOnAutofill } = useAutofill<T>(name);

    const { onKeyDownHandler, onBlurHandler, onChangeHandler, onPasteHandler } =
        useNumericField<T>({
            name,
            required
        });

    return (
        <BaseField<T, Path<T>>
            name={name}
            label={label}
            microCopy={microCopy}
            required={required}
            hideLabel={hideLabel}
            hideMicroCopy={hideMicroCopy}
        >
            {({ field, fieldState }) => {
                return (
                    <Input
                        {...field}
                        type="number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        onChange={onChangeHandler(field.value, field.onChange)}
                        onKeyDown={onKeyDownHandler}
                        onBlur={onBlurHandler(field.onBlur)}
                        onPaste={onPasteHandler}
                        id={name}
                        placeholder={placeholder}
                        autoComplete={autoComplete}
                        valid={fieldState.isValid}
                        invalid={fieldState.isInvalid}
                        disabled={disabled}
                        data-test={test}
                        ref={ref}
                        onAutofill={handleOnAutofill}
                    />
                );
            }}
        </BaseField>
    );
};

export { NumericField };
